import React, { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Bars3Icon, ChevronDownIcon } from "@heroicons/react/20/solid";
import { Link } from "react-router-dom";
import { cn } from "../utils/style";
import UserAvatar from "./UserAvatar";
import { useTranslation } from "react-i18next";

const languages = [
  {
    code: "en",
    name: "English",
    flagCode: "us",
  },
  {
    code: "ar",
    name: "العربية",
    flagCode: "ae",
  },
  {
    code: "fr",
    name: "Français",
    flagCode: "fr",
  },
];

function TopBar({
  setSidebarOpen,
  company,
  user,
  userNavigation,
  integration,
}) {
  const { i18n, t } = useTranslation();

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    document.body.dir = code === "ar" ? "rtl" : "ltr";
  };

  return (
    <div className="sticky top-0 z-40 w-full border-b border-slate-200/60 bg-white/70 backdrop-blur-xl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-x-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="group -m-2.5 p-2.5 text-slate-500 lg:hidden hover:text-primary-600 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">{t("Open sidebar")}</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            <div
              className="h-6 w-px bg-slate-200 lg:hidden"
              aria-hidden="true"
            />

            <div className="flex items-center gap-x-3 text-lg font-black tracking-tight">
              <div className="h-2 w-2 rounded-full bg-primary-500 shadow-[0_0_8px_rgba(59,130,246,0.5)] animate-pulse hidden sm:block" />
              <span className="inline uppercase text-[11px] font-black tracking-[0.2em] text-slate-400 group-hover:text-primary-600 transition-colors">
                {company?.companyType === "click2secure_validation_company"
                  ? "Validation Systems"
                  : integration?.name || "Main Integration"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-x-4 lg:gap-x-6">
            {/* Language Selector */}
            <Menu as="div" className="relative">
              <Menu.Button className="group flex items-center p-1.5 px-2.5 rounded-xl transition-all duration-300 hover:bg-slate-100/80 outline-none border border-transparent hover:border-slate-200 shadow-sm hover:shadow-md active:scale-95 bg-slate-50/50">
                <span className="sr-only">{t("Change language")}</span>
                <div className="flex items-center gap-x-2.5">
                  <div className="relative flex-shrink-0">
                    <img
                      src={`https://flagcdn.com/w40/${currentLanguage.flagCode}.png`}
                      srcSet={`https://flagcdn.com/w80/${currentLanguage.flagCode}.png 2x`}
                      width="22"
                      alt={currentLanguage.name}
                      className="rounded-sm shadow-[0_1px_3px_rgba(0,0,0,0.1)] border border-white/20 ring-1 ring-slate-900/5 object-cover h-[14px]"
                    />
                    <div className="absolute -inset-0.5 rounded-sm ring-1 ring-inset ring-black/5" />
                  </div>
                  <div className="flex items-center gap-x-1.5">
                    <span className="hidden sm:block text-[11px] font-black text-slate-600 uppercase tracking-[0.1em] group-hover:text-primary-700 transition-colors">
                      {currentLanguage.code}
                    </span>
                    <ChevronDownIcon className="h-3.5 w-3.5 text-slate-400 group-hover:text-primary-500 transition-colors" />
                  </div>
                </div>
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="transform opacity-0 scale-95 -translate-y-2"
                enterTo="transform opacity-100 scale-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="transform opacity-100 scale-100 translate-y-0"
                leaveTo="transform opacity-0 scale-95 -translate-y-2"
              >
                <Menu.Items className="absolute end-0 z-10 mt-3 w-52 origin-top-right rounded-2xl bg-white p-2 shadow-[0_20px_50px_rgba(0,0,0,0.1)] ring-1 ring-slate-200 focus:outline-none border-0 backdrop-blur-xl">
                  <div className="px-3 py-2.5 mb-2 border-b border-slate-50">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">
                      {t("Select Language")}
                    </p>
                  </div>
                  <div className="space-y-1">
                    {languages.map((lang) => (
                      <Menu.Item key={lang.code}>
                        {({ active }) => (
                          <button
                            onClick={() => changeLanguage(lang.code)}
                            className={cn(
                              "flex w-full items-center justify-between gap-3 px-3 py-2.5 text-sm font-bold transition-all duration-200 rounded-xl group/item",
                              active || i18n.language === lang.code
                                ? "bg-primary-50 text-primary-700 shadow-sm shadow-primary-100/50"
                                : "text-slate-600 hover:bg-slate-50 hover:translate-x-1"
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                <img
                                  src={`https://flagcdn.com/w40/${lang.flagCode}.png`}
                                  srcSet={`https://flagcdn.com/w80/${lang.flagCode}.png 2x`}
                                  width="24"
                                  alt={lang.name}
                                  className="rounded-sm shadow-sm object-cover h-[16px]"
                                />
                                <div className="absolute -inset-0.5 rounded-sm ring-1 ring-inset ring-black/5" />
                              </div>
                              <span className="tracking-tight">{lang.name}</span>
                            </div>
                            {i18n.language === lang.code && (
                              <div className="h-1.5 w-1.5 rounded-full bg-primary-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
                            )}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>

            <div className="h-6 w-px bg-slate-200" aria-hidden="true" />

            <Menu as="div" className="relative">
              <Menu.Button className="group flex items-center p-1 rounded-2xl transition-all duration-300 hover:bg-slate-100/50 outline-none">
                <span className="sr-only">{t("Open user menu")}</span>
                <div className="relative">
                  <UserAvatar className="h-9 w-9 rounded-xl bg-slate-100 ring-2 ring-white shadow-sm transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute -bottom-0.5 -end-0.5 h-3 w-3 rounded-full bg-emerald-500 border-2 border-white" />
                </div>
                <span className="hidden lg:flex lg:items-center">
                  <div className="ms-3 flex flex-col items-start leading-none">
                    <span
                      className="text-sm font-black text-slate-900 capitalize tracking-tight"
                      aria-hidden="true"
                    >
                      {user?.name}
                    </span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">
                      {user?.role || "Administrator"}
                    </span>
                  </div>
                  <ChevronDownIcon
                    className="ms-2.5 h-4 w-4 text-slate-400 group-hover:text-primary-500 transition-colors"
                    aria-hidden="true"
                  />
                </span>
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="transform opacity-0 scale-95 -translate-y-2"
                enterTo="transform opacity-100 scale-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="transform opacity-100 scale-100 translate-y-0"
                leaveTo="transform opacity-0 scale-95 -translate-y-2"
              >
                <Menu.Items className="absolute end-0 z-10 mt-3 w-56 origin-top-right rounded-2xl bg-white p-2 shadow-2xl shadow-slate-200/50 ring-1 ring-slate-200 focus:outline-none border-0">
                  <div className="px-3 py-2 mb-2 border-b border-slate-50">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                      {t("Account")}
                    </p>
                  </div>
                  {userNavigation.map((item) => (
                    <Menu.Item key={item.name}>
                      {({ active }) => (
                        <Link
                          to={item.href}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2.5 text-sm font-bold transition-all duration-200 rounded-xl",
                            active
                              ? "bg-primary-50 text-primary-600 translate-x-1"
                              : "text-slate-600 hover:bg-slate-50"
                          )}
                        >
                          {item.name}
                        </Link>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TopBar;
